import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Autocomplete,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import {
  getChatRoomByUuid,
  updateChatRoom,
  getParticipants,
  addParticipant,
  removeParticipant,
  updateParticipantRole,
} from '../../lib/api/chatApi';
import client from '../../lib/api/client';

/**
 * 채널 설정 다이얼로그
 */
const ChannelSettingsDialog = ({ open, onClose, chatRoom, onUpdated }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [roomInfo, setRoomInfo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 설정 편집 상태
  const [name, setName] = useState('');
  const [chatRoomTyp, setChatRoomTyp] = useState('NORMAL');

  // 사용자 초대 상태
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // 채널 정보 조회
  useEffect(() => {
    const fetchData = async () => {
      if (!chatRoom?.chatRoomUuid || !open) return;

      try {
        setLoading(true);
        const [roomData, participantData] = await Promise.all([
          getChatRoomByUuid(chatRoom.chatRoomUuid),
          getParticipants(chatRoom.chatRoomUuid)
        ]);

        setRoomInfo(roomData);
        setParticipants(participantData);
        setName(roomData.name || '');
        setChatRoomTyp(roomData.chatRoomTyp || 'NORMAL');
      } catch (error) {
        console.error('채널 정보 조회 실패:', error);
        setError('채널 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chatRoom, open]);

  // 사용자 검색
  useEffect(() => {
    const searchUsers = async () => {
      if (userSearchKeyword.length < 1) {
        setUserOptions([]);
        return;
      }

      try {
        setUserSearchLoading(true);
        const response = await client.get('/api/v1/user/search', {
          params: { keyword: userSearchKeyword }
        });
        // 이미 참여중인 사용자 제외
        const existingUuids = participants.map(p => p.usrUuid);
        const filtered = (response.data.list || []).filter(
          u => !existingUuids.includes(u.usrUuid)
        );
        setUserOptions(filtered);
      } catch (error) {
        console.error('사용자 검색 실패:', error);
        setUserOptions([]);
      } finally {
        setUserSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [userSearchKeyword, participants]);

  // 채널 정보 저장
  const handleSaveSettings = async () => {
    if (!name.trim()) {
      setError('채널 이름을 입력해주세요.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      await updateChatRoom(chatRoom.chatRoomUuid, {
        name: name.trim(),
        chatRoomTyp
      });

      onUpdated?.();
      setError('');
      alert('채널 설정이 저장되었습니다.');
    } catch (error) {
      console.error('채널 설정 저장 실패:', error);
      setError('채널 설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 사용자 초대
  const handleInviteUser = async () => {
    if (!selectedUser) return;

    try {
      await addParticipant(chatRoom.chatRoomUuid, {
        usrUuid: selectedUser.usrUuid,
        ruleTyp: 'MEMBER'
      });

      // 참여자 목록 새로고침
      const updatedParticipants = await getParticipants(chatRoom.chatRoomUuid);
      setParticipants(updatedParticipants);
      setSelectedUser(null);
      setUserSearchKeyword('');
    } catch (error) {
      console.error('사용자 초대 실패:', error);
      alert('사용자 초대에 실패했습니다.');
    }
  };

  // 참여자 제거
  const handleRemoveParticipant = async (usrUuid) => {
    if (!window.confirm('이 사용자를 채널에서 내보내시겠습니까?')) return;

    try {
      await removeParticipant(chatRoom.chatRoomUuid, usrUuid);
      setParticipants(prev => prev.filter(p => p.usrUuid !== usrUuid));
    } catch (error) {
      console.error('참여자 제거 실패:', error);
      alert('참여자 제거에 실패했습니다.');
    }
  };

  // 참여자 역할 변경
  const handleChangeRole = async (usrUuid, newRole) => {
    try {
      await updateParticipantRole(chatRoom.chatRoomUuid, usrUuid, newRole);
      setParticipants(prev => 
        prev.map(p => p.usrUuid === usrUuid ? { ...p, ruleTyp: newRole } : p)
      );
    } catch (error) {
      console.error('역할 변경 실패:', error);
      alert('역할 변경에 실패했습니다.');
    }
  };

  // 역할 표시
  const getRoleLabel = (role) => {
    switch (role) {
      case 'OWNER': return '방장';
      case 'ADMIN': return '관리자';
      default: return '멤버';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'OWNER': return 'error';
      case 'ADMIN': return 'warning';
      default: return 'default';
    }
  };

  // 현재 사용자가 관리 권한이 있는지
  const hasManagePermission = roomInfo?.myRole === 'OWNER' || roomInfo?.myRole === 'ADMIN';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">채널 설정</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 탭 */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab icon={<SettingsIcon />} label="일반" iconPosition="start" />
              <Tab icon={<GroupIcon />} label={`멤버 (${participants.length})`} iconPosition="start" />
            </Tabs>

            {/* 일반 설정 탭 */}
            {activeTab === 0 && (
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {error && (
                  <Alert severity="error" onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                <TextField
                  label="채널 이름"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  disabled={!hasManagePermission}
                />

                <FormControl fullWidth disabled={!hasManagePermission}>
                  <InputLabel>채널 유형</InputLabel>
                  <Select
                    value={chatRoomTyp}
                    onChange={(e) => setChatRoomTyp(e.target.value)}
                    label="채널 유형"
                  >
                    <MenuItem value="NORMAL">💬 일반 채널</MenuItem>
                    <MenuItem value="PROJECT">📁 프로젝트 채널</MenuItem>
                    <MenuItem value="TEAM">👥 팀 채널</MenuItem>
                  </Select>
                </FormControl>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    생성일: {roomInfo?.crtDt}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    생성자: {roomInfo?.crtNm}
                  </Typography>
                </Box>

                {hasManagePermission && (
                  <Button
                    variant="contained"
                    onClick={handleSaveSettings}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                  >
                    {saving ? '저장 중...' : '설정 저장'}
                  </Button>
                )}
              </Box>
            )}

            {/* 멤버 관리 탭 */}
            {activeTab === 1 && (
              <Box sx={{ p: 2 }}>
                {/* 사용자 초대 */}
                {hasManagePermission && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      <PersonAddIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      멤버 초대
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Autocomplete
                        sx={{ flexGrow: 1 }}
                        options={userOptions}
                        getOptionLabel={(option) => option.usrNm || option.emlAddr || ''}
                        value={selectedUser}
                        onChange={(e, newValue) => setSelectedUser(newValue)}
                        loading={userSearchLoading}
                        onInputChange={(e, value) => setUserSearchKeyword(value)}
                        isOptionEqualToValue={(option, value) => option.usrUuid === value.usrUuid}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="이름 또는 이메일로 검색..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {userSearchLoading && <CircularProgress size={16} />}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                                {option.usrNm?.charAt(0) || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2">{option.usrNm}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.emlAddr}
                                </Typography>
                              </Box>
                            </Box>
                          </li>
                        )}
                      />
                      <Button
                        variant="contained"
                        onClick={handleInviteUser}
                        disabled={!selectedUser}
                      >
                        초대
                      </Button>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* 참여자 목록 */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  참여자 목록
                </Typography>
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {participants.map((participant) => (
                    <ListItem
                      key={participant.usrUuid}
                      sx={{ borderRadius: 1, mb: 0.5, bgcolor: 'action.hover' }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ width: 36, height: 36 }}>
                          {participant.usrNm?.charAt(0) || '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">{participant.usrNm}</Typography>
                            <Chip
                              label={getRoleLabel(participant.ruleTyp)}
                              size="small"
                              color={getRoleColor(participant.ruleTyp)}
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Box>
                        }
                        secondary={participant.usrEmlAddr || participant.joinedAt}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      {hasManagePermission && participant.ruleTyp !== 'OWNER' && (
                        <ListItemSecondaryAction>
                          {/* 역할 변경 */}
                          <IconButton
                            size="small"
                            onClick={() => handleChangeRole(
                              participant.usrUuid,
                              participant.ruleTyp === 'ADMIN' ? 'MEMBER' : 'ADMIN'
                            )}
                            title={participant.ruleTyp === 'ADMIN' ? '관리자 해제' : '관리자 지정'}
                          >
                            <AdminIcon 
                              fontSize="small" 
                              color={participant.ruleTyp === 'ADMIN' ? 'warning' : 'action'} 
                            />
                          </IconButton>
                          {/* 내보내기 */}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveParticipant(participant.usrUuid)}
                            title="내보내기"
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChannelSettingsDialog;
